import {
  ChangeDetectionStrategy,
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  inject,
  input,
} from '@angular/core'
import { LoggerFactory } from '@elderbyte/ts-logger'
import {
  DataContextBuilder,
  ElderToastService,
  Filter,
  IDataContext,
  Sort,
  ElderTableComponent,
  ElderTableToolbarDirective,
  ElderDataToolbarComponent,
  ElderToolbarContentDirective,
  ElderSearchBoxComponent,
  ElderSearchContextDirective,
  ElderSearchPanelComponent,
  ElderSearchInputDirective,
  ElderSelectComponent,
  ElderNumberCellDirective,
  ElderQuantityPipe,
  ElderSinglePaneWrapperComponent,
} from '@elderbyte/ngx-starter'
import { Router } from '@angular/router'
import { MatDialog } from '@angular/material/dialog'
import { Subject } from 'rxjs'
import { Package } from '../package'
import { PackageService } from '../package.service'
import { PackageFilterBuilder } from '../package-filter-builder'
import {
  IPackageDialogOptions,
  PackageDetailDialogComponent,
} from '../package-detail-dialog/package-detail-dialog.component'
import { LocationFilter } from '../../location-filter'
import { ElderTranslatePipe } from '@elderbyte/ngx-starter'
import { DatePipe } from '@angular/common'
import { MatIcon } from '@angular/material/icon'
import {
  MatColumnDef,
  MatHeaderCellDef,
  MatHeaderCell,
  MatCellDef,
  MatCell,
} from '@angular/material/table'
import { PackageStateSelectDirective } from '../package-state-select/package-state-select.directive'
import { FormsModule } from '@angular/forms'
import { MatInput } from '@angular/material/input'
import { MatFormField, MatLabel } from '@angular/material/form-field'
import { MatSort, MatSortHeader } from '@angular/material/sort'

@Component({
  selector: 'app-package-browser',
  templateUrl: './package-browser.component.html',
  styleUrls: ['./package-browser.component.scss'],
  imports: [
    ElderTableComponent,
    MatSort,
    ElderTableToolbarDirective,
    ElderDataToolbarComponent,
    ElderToolbarContentDirective,
    ElderSearchBoxComponent,
    ElderSearchContextDirective,
    ElderSearchPanelComponent,
    MatFormField,
    MatLabel,
    MatInput,
    FormsModule,
    ElderSearchInputDirective,
    ElderSelectComponent,
    PackageStateSelectDirective,
    MatColumnDef,
    MatHeaderCellDef,
    MatHeaderCell,
    MatCellDef,
    MatCell,
    MatIcon,
    MatSortHeader,
    ElderNumberCellDirective,
    DatePipe,
    ElderTranslatePipe,
    ElderQuantityPipe,
    ElderSinglePaneWrapperComponent,
  ],
})
export class PackageBrowserComponent implements OnInit, OnDestroy {
  /***************************************************************************
   *                                                                         *
   * Fields                                                                  *
   *                                                                         *
   **************************************************************************/

  private readonly log = LoggerFactory.getLogger(this.constructor.name)

  private readonly _unsubscribe$ = new Subject<void>()

  private _storageGroupIdFilter: string

  public readonly data: IDataContext<Package>

  public readonly float = input<boolean>(false)

  public readonly embedded = input<boolean>(false)

  public readonly canAdd = input<boolean>(false)

  public readonly canRemove = input<boolean>(false)

  public readonly openDetailInDialog = input<boolean>(false)

  @Output()
  public requestNew: EventEmitter<void> = new EventEmitter<void>()

  private router = inject(Router)

  private toastService = inject(ElderToastService)

  private dialog = inject(MatDialog)

  /***************************************************************************
   *                                                                         *
   * Constructor                                                             *
   *                                                                         *
   **************************************************************************/

  constructor() {
    const packageService = inject(PackageService)
    this.data = DataContextBuilder.start<Package>()
      .pageSize(50)
      .reloadOnLocalChanges()
      .build(packageService)
  }

  /***************************************************************************
   *                                                                         *
   * Life Cycle                                                              *
   *                                                                         *
   **************************************************************************/

  public ngOnInit(): void {
    this.data.start([new Sort('createdAt', 'desc')], this.data.filter.filtersSnapshot)
  }

  public ngOnDestroy(): void {
    this._unsubscribe$.next()
    this._unsubscribe$.complete()

    if (this.data) {
      this.data.close()
    }
  }

  /***************************************************************************
   *                                                                         *
   * Properties                                                              *
   *                                                                         *
   **************************************************************************/

  @Input()
  public set storageGroupIdFilter(storageGroupId: string) {
    this._storageGroupIdFilter = storageGroupId
    this.updateFilters(PackageFilterBuilder.storageGroupIds(storageGroupId))
  }

  public get storageGroupIdFilter(): string {
    return this._storageGroupIdFilter
  }

  @Input()
  public set locationFilter(filter: LocationFilter) {
    this.log.info('setting location filter:', filter)
    this.updateFilters(
      ...PackageFilterBuilder.storagePath(filter.storagePathString, filter.deepSearch)
    )
  }

  /***************************************************************************
   *                                                                         *
   * Public API                                                              *
   *                                                                         *
   **************************************************************************/

  public onRequestNew() {
    this.requestNew.emit()
  }

  public openSideDetail(pkg: Package): void {
    this.toastService.info('Not implemented!')
  }

  public openDetail(pkg: Package): void {
    if (pkg) {
      if (this.openDetailInDialog()) {
        this.openDetailDialog(pkg)
      } else {
        this.router.navigate([
          {
            outlets: {
              side: ['packages', pkg.id],
            },
          },
        ])
      }
    }
  }

  /***************************************************************************
   *                                                                         *
   * Private Methods                                                         *
   *                                                                         *
   **************************************************************************/

  private openDetailDialog(pkg: Package): void {
    const dialogRef = this.dialog.open(PackageDetailDialogComponent, {
      maxWidth: '95vw',
      maxHeight: '95vw',
      width: '95vw',
      height: '95vh',
      data: <IPackageDialogOptions>{
        pkg: pkg,
      },
    })
  }

  private updateFilters(...filters: Filter[]): void {
    if (this.data) {
      this.log.info('Updating filters', filters)
      this.data.filter.updateFilters(filters)
    }
  }
}
